export interface ScheduleCalendar {
  id: string;
  userId: string;
  name: string;
  calendarType: string;
  timeZone: string;
}

export interface ScheduledItem {
  id: string;
  calendarId: string;
  title: string;
  description: string | null;
  startsAt: string;
  endsAt: string;
  localDate: string;
  localStartMinute: number;
  localEndMinute: number;
}

export interface CreateCalendarRequest {
  userId: string;
  name: string;
  timeZone: string;
  calendarType: string;
}

export interface CreateScheduledItemRequest {
  userId: string;
  calendarId: string;
  name: string;
  description: string | null;
  date: string;
  startMinute: number;
  endMinute: number;
}

const API_URL = "/api";

export async function getCalendars(userId: string) {
  const searchParams = new URLSearchParams({ userId });
  const data = await request<unknown>(
    `${API_URL}/calendars?${searchParams}`,
    undefined,
    { notFound: "empty" },
  );
  return asArray(data).map(toScheduleCalendar);
}

export async function createCalendar(requestBody: CreateCalendarRequest) {
  const data = await request<unknown>(`${API_URL}/calendars`, {
    method: "POST",
    body: JSON.stringify({
      userId: requestBody.userId,
      name: requestBody.name,
      calendarType: requestBody.calendarType,
      timeZoneId: requestBody.timeZone,
    }),
  });

  return toScheduleCalendar(data);
}

export async function getScheduledItems(calendarId: string, userId: string) {
  const searchParams = new URLSearchParams({ userId });
  const data = await request<unknown>(
    `${API_URL}/calendars/${encodeURIComponent(calendarId)}/schedule?${searchParams}`,
    undefined,
    { notFound: "empty" },
  );

  return asArray(data).map(toScheduledItem);
}

export async function createScheduledItem(
  requestBody: CreateScheduledItemRequest,
) {
  const data = await request<unknown>(
    `${API_URL}/calendars/${encodeURIComponent(requestBody.calendarId)}/schedule`,
    {
      method: "POST",
      body: JSON.stringify({
        userId: requestBody.userId,
        name: requestBody.name,
        description: requestBody.description,
        date: requestBody.date,
        startMinute: requestBody.startMinute,
        endMinute: requestBody.endMinute,
      }),
    },
  );

  if (isRecord(data) && !("startsAtUtc" in data)) {
    return {
      id: readString(data, "scheduledItemId", "id"),
      calendarId: requestBody.calendarId,
      title: requestBody.name,
      description: requestBody.description,
      startsAt: minuteToDateTime(requestBody.date, requestBody.startMinute),
      endsAt: minuteToDateTime(requestBody.date, requestBody.endMinute),
      localDate: requestBody.date,
      localStartMinute: requestBody.startMinute,
      localEndMinute: requestBody.endMinute,
    };
  }

  return toScheduledItem(data);
}

export async function deleteScheduledItem(
  calendarId: string,
  scheduledItemId: string,
  userId: string,
) {
  const searchParams = new URLSearchParams({ userId });

  await request<void>(
    `${API_URL}/calendars/${encodeURIComponent(calendarId)}/schedule/${encodeURIComponent(scheduledItemId)}?${searchParams}`,
    {
      method: "DELETE",
    },
  );
}

async function request<T>(
  url: string,
  init?: RequestInit,
  options?: { notFound?: "empty" },
): Promise<T> {
  const response = await fetch(url, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...init?.headers,
    },
  });

  if (response.status === 404 && options?.notFound === "empty") {
    return [] as T;
  }

  if (!response.ok) {
    throw new Error(`Schedule request failed with ${response.status}`);
  }

  if (response.status === 204) {
    return null as T;
  }

  return response.json() as Promise<T>;
}

function asArray(data: unknown) {
  if (Array.isArray(data)) {
    return data;
  }

  if (isRecord(data) && Array.isArray(data.items)) {
    return data.items;
  }

  return [];
}

function toScheduleCalendar(data: unknown): ScheduleCalendar {
  if (!isRecord(data)) {
    throw new Error("Invalid calendar response");
  }

  return {
    id: readString(data, "id", "calendarId"),
    userId: readString(data, "userId", "ownerUserId"),
    name: readString(data, "name", "title"),
    calendarType: readString(data, "calendarType", "typeId"),
    timeZone: readString(data, "timeZoneId", "timeZone", "timezone", "time_zone"),
  };
}

function toScheduledItem(data: unknown): ScheduledItem {
  if (!isRecord(data)) {
    throw new Error("Invalid scheduled item response");
  }

  return {
    id: readString(data, "id", "scheduledItemId"),
    calendarId: readString(data, "calendarId"),
    title: readString(data, "name", "title"),
    description: readNullableString(data, "description"),
    startsAt: readString(data, "startsAtUtc", "startsAt", "start"),
    endsAt: readString(data, "endsAtUtc", "endsAt", "end"),
    localDate: readString(data, "localDate"),
    localStartMinute: readNumber(data, "localStartMinute"),
    localEndMinute: readNumber(data, "localEndMinute"),
  };
}

function readString(data: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) {
    const value = data[key];

    if (typeof value === "string") {
      return value;
    }

    if (typeof value === "number") {
      return value.toString();
    }
  }

  return "";
}

function readNumber(data: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) {
    const value = data[key];

    if (typeof value === "number") {
      return value;
    }

    if (typeof value === "string") {
      const parsed = Number(value);

      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }

  return 0;
}

function readNullableString(data: Record<string, unknown>, ...keys: string[]) {
  const value = readString(data, ...keys);
  return value || null;
}

function minuteToDateTime(date: string, minute: number) {
  if (minute === 1440) {
    const nextDate = new Date(`${date}T00:00:00`);
    nextDate.setDate(nextDate.getDate() + 1);
    return `${nextDate.toISOString().slice(0, 10)}T00:00:00`;
  }

  const hours = Math.floor(minute / 60)
    .toString()
    .padStart(2, "0");
  const minutes = (minute % 60).toString().padStart(2, "0");
  return `${date}T${hours}:${minutes}:00`;
}

function isRecord(data: unknown): data is Record<string, unknown> {
  return typeof data === "object" && data !== null;
}
